<template>
    <v-main class="pt-0">
        <cover-image src="/images/covers/home-cover-desktop.jpg"
                     phone-src="/images/covers/home-cover-phone.jpg"
                     tablet-src="/images/covers/home-cover-tablet.jpg"
                     color="#298c9e" secondary-color="#d15914"
                     title="Leila Krejci" subtitle="Gesang • Musikproduktion • Entertainment"
                     description="Hey :-) Schön, dass du da bist!<br/>Ich bin Leila, Musikerin aus München."
                     action-text="Let's go!"
                     :action-path="`#${sections[0].id}`"
                     :next-section-id="sections[0].id"
                     full-height
                     left
                     main>
            <template #actions>
                <div class="d-flex align-center mt-32">
                    <v-btn v-for="(social, socialKey) in socialLinks" :key="socialKey" variant="text" color="white"
                           :icon="social.icon"
                           size="x-large" :href="social.url" rel="noopener" target="_blank" :title="social.title"/>
                </div>
            </template>
        </cover-image>

        <cover-image v-for="(section, sectionIndex) in sections" :key="sectionIndex"
                     :id="section.id"
                     :title="section.title"
                        :subtitle="section.subtitle"
                        :description="section.description"
                     :src="section.src"
                     :phone-src="section.phoneSrc"
                     :tablet-src="section.tabletSrc"
                     :color="section.color"
                     :secondary-color="section.secondaryColor"
                     :next-section-id="sectionIndex < sections.length - 1 ? sections[sectionIndex + 1].id : undefined"
                     full-height>
            <template v-if="section.buttons?.length" #buttons>
                <v-btn v-for="(button, buttonIndex) in section.buttons"
                       :key="buttonIndex"
                       :to="button.path"
                       :color="button.color ?? section.color"
                       :disabled="button.disabled"
                       size="x-large"
                       :append-icon="button.icon"
                       :variant="button.variant">
                    {{ button.text}}
                </v-btn>
            </template>

            <template v-if="section.id === 'link-with-leila'" #actions>

                <wrapper class="mb-32" stretch>
                    <v-btn v-for="(social, socialKey) in socialLinks" :key="socialKey" variant="outlined"
                           :color="section.secondaryColor"
                           :prepend-icon="social.icon"
                           :text="social.title"
                           size="large" :href="social.url" rel="noopener" target="_blank" :title="social.title"/>
                </wrapper>

                <v-alert v-if="hasClickedOnContactButton" color="#6b6df1"
                         class="contact-form__alert"
                         border="left"
                         elevation="2"
                         colored-border>
                    Vielen Dank!<br>Dein E-Mail-Programm zum Senden der Nachricht sollte sich soeben geöffnet haben.
                    Falls dies nicht der Fall sein sollte, schicke Deine Nachricht bitte an <b>{{ email }}</b>.
                    <br/><br/>
                    <i>{{ contactData.message }}</i>
                </v-alert>

                <v-form v-else @submit.prevent="handleContactSubmit">
                    <v-textarea class="contact-form__message" v-model="contactData.message" rows="7" label="Deine Nachricht"
                                variant="solo"
                                required/>

                    <v-btn type="submit" :color="section.secondaryColor" class="w-full" size="large" :prepend-icon="mdiSendOutline">
                        Kontaktieren
                    </v-btn>

                </v-form>

            </template>
        </cover-image>

        <app-footer dark absolute hide-social/>
    </v-main>
</template>

<script setup lang="ts">
import socialLinks from "../config/social";
import {mdiArrowRight, mdiSendOutline} from "@mdi/js";
import {email} from "../config/main";
import {IndexPageSection} from "../types/pages";

const sections: IndexPageSection[] = [
    {
        id: 'learn-about-leila',
        title: 'Learn about Leila',
        subtitle: 'Bio',
        description: 'Musik ist eine 360-Grad-Vision für mich. Sängerin bin ich aus Leidenschaft und Produzentin aus Überzeugung!<br/>Die Musikbranche braucht mehr FLINTA-Personen - aktuell ist der Anteil in vielen Bereichen einstellig und damit viel zu gering. Um diesen Wandel aktiv mitzugestalten, studiere ich an der Point Blank Music School, um das Music Production Complete Diploma abzuschließen.<br/><br/>Neben dem kreativen Schaffen kenne ich auch das professionelle Business: Seit 2024 arbeite ich als Strategieberaterin in einer Musikmarketing-Agentur und begleite Acts dabei, ihre Musikkarriere professionell weiterzuentwickeln. ',
        src: '/images/covers/about-cover-desktop.jpg',
        phoneSrc: '/images/covers/about-cover-phone.jpg',
        tabletSrc: '/images/covers/about-cover-tablet.jpg',
        color: '#040605',
        secondaryColor: '#dc1309',
    },
    {
        id: 'look-at-leila',
        title: 'Look at Leila',
        subtitle: 'Model',
        description: 'Kreativität kennt keine Grenzen :-) Neben der Musik brenne ich für Projekte in den Bereichen Modeln, Schauspiel und Entertainment. Check gerne mal aus, was ich zuletzt gemacht habe!',
        src: '/images/covers/graffiti-cover-desktop.jpg',
        phoneSrc: '/images/covers/graffiti-cover-phone.jpg',
        tabletSrc: '/images/covers/graffiti-cover-tablet.jpg',
        color: '#FF8A2F',
        secondaryColor: '#ffc947',
        buttons: [
            {
                text: 'Photo Shoots',
                path: '/photo-shoots',
                color: '#FF8A2F',
                icon: mdiArrowRight,
            },
            {
                text: 'Video Shoots',
                path: '/video-shoots',
                color: '#ffc947',
                icon: mdiArrowRight,
            },
        ]
    },
    {
        id: 'listen-to-leila',
        title: 'Listen to Leila',
        subtitle: 'Musikerin',
        description: 'Von meinem Home-Studio direkt in deine Playlist – mein erster Release kommt 2026!',
        src: '/images/covers/gitarre-cover-desktop.jpg',
        phoneSrc: '/images/covers/gitarre-cover-phone.jpg',
        tabletSrc: '/images/covers/gitarre-cover-tablet.jpg',
        color: '#692b12',
        secondaryColor: '#b88e76',
        buttons: [
            {
                text: 'In Kürze verfügbar',
                disabled: true
            },
        ]
    },

    {
        id: 'link-with-leila',
        title: 'Link with Leila',
        subtitle: 'Get in touch',
        src: '/images/covers/laugh-cover-desktop.jpg',
        phoneSrc: '/images/covers/laugh-cover-phone.jpg',
        tabletSrc: '/images/covers/laugh-cover-tablet.jpg',
        color: '#aa0035',
        secondaryColor: '#6b6df1',

    },
];

const contactData = ref({});
const hasClickedOnContactButton = ref(false);

function handleContactSubmit() {
    const subject = encodeURIComponent('Kontaktanfrage über leilakrejci.com')
    const body = encodeURIComponent(contactData.value.message);

    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
    hasClickedOnContactButton.value = true;
}
</script>

<style lang="scss">
.contact-form__message, .contact-form__alert {
    min-width: 400px;

    @media #{map-get($display-breakpoints, 'sm-and-down')} {
        min-width: 320px;
    }
}
</style>
